import React, { Component } from "react";
import { Paper, Typography } from "@material-ui/core";
import moment from "moment";
import Constants from "helpers/constants";
import { Redirect } from "react-router-dom";
import FilePreviewModal from "components/FilePreviewModal";
import Outline from "../assets/img/-outline.svg";
import eye from "../assets/img/eye.svg";
import SmallLoader from "./SmallLoader";

interface ProjectAnnouncementsCardProps {
  announcements?: any;
  authUser?: string | any;
  chainPermission?: string | any;
  isLoading?: boolean;
}

interface ProjectAnnouncementsCardState {
  redirect: boolean;
  previewModal: {
    isOpen: boolean;
    url: string;
    name: string;
  };
  minTimeElapsed: boolean;
}

class ProjectAnnouncementsCard extends Component<
  ProjectAnnouncementsCardProps,
  ProjectAnnouncementsCardState
> {
  private loadingTimer: NodeJS.Timeout | null = null;

  constructor(props: ProjectAnnouncementsCardProps) {
    super(props);
    this.state = {
      redirect: false,
      previewModal: {
        isOpen: false,
        url: "",
        name: "",
      },
      minTimeElapsed: false,
    };
  }

  componentDidMount() {
    this.loadingTimer = setTimeout(() => {
      this.setState({ minTimeElapsed: true });
    }, 6000);
  }

  componentWillUnmount() {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
    }
  }

  handleClick = () => {
    this.setState({ redirect: true });
  };

  isPreviewable = (url: string): boolean => {
    const extension = url.split(".").pop()?.toLowerCase() || "";
    const previewableTypes = ["jpg", "jpeg", "png", "gif", "webp", "pdf"];
    return previewableTypes.includes(extension);
  };

  handleAttachmentClick = (url: string, name?: string, index?: number, announcementId?: string | number) => {
    const fileName = name || url.split("/").pop() || "File";
    // Build API URL with file index for correct attachment
    const apiUrl = announcementId && typeof index === 'number'
      ? `${process.env.REACT_APP_API_SERVICE_URL || ''}/api/announcement-file/${announcementId}/${index}`
      : url;
    if (this.isPreviewable(url)) {
      // Open preview modal for images and PDFs
      this.setState({
        previewModal: {
          isOpen: true,
          url: apiUrl,
          name: fileName,
        },
      });
    } else {
      // Direct download for other file types
      this.handleDownload(apiUrl, fileName);
    }
  };

  handleDownload = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  handleClosePreview = () => {
    this.setState({
      previewModal: {
        isOpen: false,
        url: "",
        name: "",
      },
    });
  };

  render() {
    const { announcements = [], authUser, chainPermission, isLoading } = this.props;
    const { redirect, previewModal, minTimeElapsed } = this.state;

    // Show loader if isLoading is true OR if minimum time hasn't elapsed yet
    const showLoader = isLoading || !minTimeElapsed;

    if (
      redirect &&
      (chainPermission == Constants.AB_SYSTEM_ADMIN ||
        chainPermission == Constants.INTERNAL_PROJECT_ADMIN ||
        chainPermission == Constants.EXTERNAL_PROJECT_ADMIN)
    ) {
      return <Redirect to="/projects-new/announcements-new" />;
    }
    if(redirect){
      return <Redirect to="/projects-new/announcements/me" />;
    }

    const cardStyle: React.CSSProperties = {
      flex: "1 1 300px",
      backgroundColor: "#FFFFFF",
      borderRadius: "16px",
      padding: "16px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      height: "100%",
      background: "linear-gradient(128.26deg, #FFFFFF 61.03%, #E3BD21 153.87%)",
      border: "1px solid #E3E6EA",
    };

    const titleStyle: React.CSSProperties = {
      fontSize: "14px",
      fontWeight: 600,
      color: "#333",
    };

    const announcementItemStyle: React.CSSProperties = {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      padding: "0 0 12px 0",
      borderBottom: "2px dotted #e8e8e8",
    };

    const announcementTitleStyle: React.CSSProperties = {
      fontSize: "12px",
      fontWeight: 500,
      color: "#000",
      marginTop: "4px",
    };

    const announcementMetaStyle: React.CSSProperties = {
      fontSize: "12px",
      color: "#A0A0A0",
    };

    const attachmentButtonStyle: React.CSSProperties = {
      width: "16px",
      height: "16px",
      cursor: "pointer",
    };

    const userRole = authUser?.role;

    return (
      <Paper style={cardStyle}>
        <Typography variant="h6" style={titleStyle}>
          Project Announcements
        </Typography>

        {showLoader ? (
          <div
            style={{
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SmallLoader />
          </div>
        ) : announcements.length === 0 ? (
          <div
            style={{
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography style={announcementMetaStyle}>No announcements available.</Typography>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#ccc transparent",
            }}
          >
            {announcements.map((announcement: any, index: any) => (
              <div
                key={index}
                style={announcementItemStyle}
                // onClick={this.handleClick}
              >
                <Typography style={announcementTitleStyle}>{announcement.title}</Typography>
                <Typography style={announcementMetaStyle}>{announcement?.chain.name}</Typography>
                <Typography style={announcementMetaStyle}>
                  {moment(announcement.dateCreated).format("MM/DD/YYYY")}
                </Typography>
                <Typography style={announcementMetaStyle}>
                  {announcement?.user.firstName && `By ${announcement?.user.lastName} `}
                </Typography>
                <Typography style={announcementMetaStyle}>
                  {announcement?.body
                    ? (() => {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(announcement.body, "text/html");
                        return doc.body.textContent || "";
                      })()
                    : ""}
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    justifyContent: "flex-start",
                  }}
                >
                  {announcement.allAttachments?.map((item: any, idx: number) => {
                    const canPreview = this.isPreviewable(item.name);
                    const apiUrl = announcement.id !== undefined ? `${process.env.REACT_APP_API_SERVICE_URL || ''}/api/announcement-file/${announcement.id}/${idx}` : item.name;

                    return (
                      <div
                        style={attachmentButtonStyle}
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.handleAttachmentClick(apiUrl, item.name, idx, announcement.id);
                        }}
                        title={`${item.name} - ${canPreview ? "Click to preview" : "Click to download"}`}
                      >
                        <img
                          src={canPreview ? eye : Outline}
                          style={{ width: "100%", height: "100%" }}
                          alt=""
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <FilePreviewModal
          isOpen={previewModal.isOpen}
          fileUrl={previewModal.url}
          fileName={previewModal.name}
          onClose={this.handleClosePreview}
        />
      </Paper>
    );
  }
}
export default ProjectAnnouncementsCard;
